import { Api } from '@/services';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface Tenant {
  id: string;
  name: string;
  type: string;
  description: string | null;
}

export interface AuthState {
  loggedIn: boolean;
  isLoading: boolean;
  error: Error | null;
  accessToken: string | null;
  refreshToken: string | null;
  currentTenant: Tenant | null;
}

/**
 * Define the initial auth state
 */
const initialAuthState: Partial<AuthState> = {
  loggedIn: false,
  isLoading: false,
  accessToken: null,
  refreshToken: null,
  error: null,
  currentTenant: null,
};

/**
 * Define the intiaial auth slice object
 * @resources
 * https://redux-toolkit.js.org/api/createSlice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,

  /**
   * Define the reducers for this slice
   */
  reducers: {
    logout: (state) => {
      state.loggedIn = initialAuthState.loggedIn;
      state.accessToken = initialAuthState.accessToken;
      state.refreshToken = initialAuthState.refreshToken;
      // Don't clear the current tenant on logout
    },
    login: (state, action: PayloadAction<Pick<AuthState, 'accessToken' | 'refreshToken'>>) => {
      state = { ...state, loggedIn: true, ...action.payload };
    },
    setError: (state, action: PayloadAction<Error | null>) => {
      state.error = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    tokenUpdated: (state, action: PayloadAction<Pick<AuthState, 'accessToken' | 'refreshToken'>>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
    setCurrentTenant: (state, action: PayloadAction<Tenant | null>) => {
      state.currentTenant = action.payload;
    },
  },

  /**
   * For syncing with rtk-query, updating the local state when a query fetches
   */
  extraReducers: (builder) => {
    /**
     * When user signs in, set the local state to the returned private user data
     */
    builder.addMatcher(Api.endpoints.postAuthLogin.matchFulfilled, (_state, action) => {
      return { ...action.payload.data, loggedIn: true };
    });
  },
});

/**
 * Export the corresponding redux actions
 */
export const { logout, login, setCurrentTenant } = authSlice.actions;
export default authSlice.reducer;
